import {
  AvailablePatternIconEnum,
  type Mission,
  type MissionSettings,
  MissionTypeEnum,
} from '@fleet-bffs/api-contract';
import formatMessageWrapper from '~/utils/format-message-wrapper/format-message-wrapper';
import { AvailablePattern, type PatternSetting } from '~/data/Asset';
import type { IconName } from '~uikit/Icon/icon-names';

export const availableRoboticMowersPatterns = [
  {
    orientationShift: 0,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.parallel',
    cuttingPatternIconType: AvailablePatternIconEnum.Parallel0,
  },
  {
    orientationShift: 90,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.checkerboard',
    cuttingPatternIconType: AvailablePatternIconEnum.Checkerboard90,
  },
  {
    orientationShift: 60,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.triangle',
    cuttingPatternIconType: AvailablePatternIconEnum.Triangle60,
  },
  {
    orientationShift: 120,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.diamond',
    cuttingPatternIconType: AvailablePatternIconEnum.Diamond120,
  },
  {
    orientationShift: 135,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.diamond',
    cuttingPatternIconType: AvailablePatternIconEnum.Rotation135,
  },
  {
    orientationShift: 150,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.diamond',
    cuttingPatternIconType: AvailablePatternIconEnum.Rotation150,
  },
  {
    orientationShift: 45,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.rotation',
    cuttingPatternIconType: AvailablePatternIconEnum.Rotation45,
  },
  {
    orientationShift: 30,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.rotation',
    cuttingPatternIconType: AvailablePatternIconEnum.Rotation30,
  },
  {
    orientationShift: 15,
    cuttingPatternTranslationKey: 'bff.robotic_mower_cutting_pattern.rotation',
    cuttingPatternIconType: AvailablePatternIconEnum.Rotation15,
  },
];

/**
 * Converts google-formatted (0=north,direction=CW) orientation angle to the
 * vehicle coordinate system angle (0=east,direction=CCW).
 * Should be used when exporting google-map orientation.
 */
export const exportOrientation = (googleValue) => {
  if (googleValue <= 90) {
    return 90 - googleValue;
  }
  return 360 - (googleValue - 90);
};

/**
 * Converts vehicle coordinate system angle (0=east,direction=CCW) to the
 * google-formatted (0=north,direction=CW) orientation angle.
 * Should be used during transfer of the BFF orientation value to google-map.
 */
export const importOrientation = (bffValue) => {
  if (bffValue <= 90) {
    return Math.round((90 - bffValue) * 100) / 100;
  }
  return Math.round((360 - (bffValue - 90)) * 100) / 100;
};

export const defaultInitialPatternSettings: PatternSetting = {
  orientation: 0,
  orientationShift: 0,
  cuttingPatternTranslationKey: '',
  cuttingPatternIconType: AvailablePatternIconEnum.Parallel0,
  borderMowingType: null,
};

export const shouldDisplayOrientationShift = (orientationShift) => {
  return orientationShift > 0 && orientationShift !== 90;
};

export const formatDisplayOrientationShift = (orientationShift) => {
  return orientationShift >= 120 ? 180 - orientationShift : orientationShift;
};

export const getMowingPatternSelectOptions = (
  options: AvailablePattern[],
  randomMissionCapable?: boolean
) => {
  const random = {
    optionTitle: formatMessageWrapper('web.pattern_selector.modal.controls.mowing_mode.random'),
    options: [
      {
        cuttingPatternIconType: 'random',
        cuttingPatternTranslationKey: 'web.pattern_selector.modal.controls.mowing_mode.random',
        orientationShift: 0,
      },
    ],
  };
  const systematic = {
    optionTitle: formatMessageWrapper(
      'web.pattern_selector.modal.controls.mowing_mode.systematic_patterns_list_title'
    ),
    options,
  };
  return randomMissionCapable
    ? {
        random,
        systematic,
      }
    : {
        systematic,
      };
};

const getSystematicIcon = (
  settings?: Pick<MissionSettings, 'pattern'>
): IconName | undefined => {
  return settings?.pattern?.cuttingPatternIconType
    ? (`pattern_${settings?.pattern.cuttingPatternIconType}` as IconName)
    : undefined;
};
export const getMissionPatternIcon = (
  mission?: Pick<Mission, 'type'> & { settings?: Pick<MissionSettings, 'pattern'> }
) =>
  mission?.type == MissionTypeEnum.Random ? 'pattern_random' : getSystematicIcon(mission?.settings);
